import React, { useState, useEffect, useMemo } from 'react';
import pedidoService from '../services/pedidoService';
import { useAuth } from '../contexts/AuthContext';
import { useDialog } from '../contexts/DialogContext';
import LoadingSpinner from '../components/LoadingSpinner';
import './Pedidos.css';

const obtenerNumeroPedidoVisible = (pedido) => {
  if (!pedido || pedido.tipoPedido === 'en_stock') return '';
  const num = String(pedido.numeroPedido || '').toUpperCase();
  const matchViejo = num.match(/^([PCE])-([A-F0-9]{8})-(\d{4})$/);
  if (matchViejo) {
    return `${matchViejo[1]}-${matchViejo[3]}`;
  }
  return num;
};

const Pedidos = () => {
  const { user } = useAuth();
  const { showAlert, showConfirm } = useDialog();
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [busquedaNumero, setBusquedaNumero] = useState('');
  const [modalVerAbierto, setModalVerAbierto] = useState(false);
  const [pedidoSeleccionado, setPedidoSeleccionado] = useState(null);
  const [diasLimiteTemp, setDiasLimiteTemp] = useState('');
  const [guardandoPago, setGuardandoPago] = useState(false);
  const [nuevoPago, setNuevoPago] = useState({
    monto: '',
    metodo: 'efectivo',
    fecha: new Date().toISOString().split('T')[0],
    referencia: ''
  });
  
  // Estados de paginación
  const [paginaActual, setPaginaActual] = useState(1);
  const [itemsPorPagina, setItemsPorPagina] = useState(10);

  // Cargar pedidos
  const cargarPedidos = async () => {
    try {
      setLoading(true);
      const response = await pedidoService.getPedidos();
      setPedidos(response.data || []);
      setPaginaActual(1); // Resetear a página 1 al recargar
    } catch (error) {
      console.error('Error cargando pedidos:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarPedidos();
  }, []);

  // Ver detalle del pedido
  const handleVerPedido = async (id) => {
    try {
      const response = await pedidoService.getPedidoById(id);
      setPedidoSeleccionado(response.data);
      setNuevoPago((prev) => ({
        ...prev,
        monto: '',
        fecha: new Date().toISOString().split('T')[0],
        referencia: ''
      }));
      setModalVerAbierto(true);
    } catch (error) {
      console.error('Error cargando pedido:', error);
    }
  };

  // Cancelar pedido
  const handleCancelarPedido = async (id) => {
    const confirmado = await showConfirm('¿Está seguro de cancelar este pedido? Si es pedido en stock, se devolverá el inventario.', {
      title: 'Cancelar pedido',
      confirmLabel: 'Cancelar pedido',
    });
    if (!confirmado) return;

    try {
      await pedidoService.cancelarPedido(id);
      cargarPedidos();
    } catch (error) {
      console.error('Error cancelando pedido:', error);
    }
  };

  const handleEliminarPedidoCancelado = async (id) => {
    const confirmado = await showConfirm('¿Eliminar este pedido cancelado de forma definitiva?', {
      title: 'Eliminar pedido cancelado',
      confirmLabel: 'Eliminar definitivamente'
    });
    if (!confirmado) return;

    try {
      await pedidoService.eliminarPedidoCancelado(id);
      if (modalVerAbierto && pedidoSeleccionado?._id === id) {
        setModalVerAbierto(false);
      }
      await cargarPedidos();
      showAlert('Pedido cancelado eliminado correctamente', { type: 'success' });
    } catch (error) {
      console.error('Error eliminando pedido cancelado:', error);
      showAlert('Error: ' + (error.response?.data?.message || error.message), { type: 'error' });
    }
  };

  // Actualizar días límite
  const handleActualizarDiasLimite = async (id, diasLimite) => {
    try {
      const response = await pedidoService.actualizarDiasLimite(id, diasLimite);
      showAlert(response.message || 'Fecha límite actualizada', { type: 'success' });
      cargarPedidos();
      // Actualizar el pedido seleccionado en el modal
      const responseActualizado = await pedidoService.getPedidoById(id);
      setPedidoSeleccionado(responseActualizado.data);
    } catch (error) {
      console.error('Error actualizando días límite:', error);
      showAlert('Error: ' + (error.response?.data?.message || error.message), { type: 'error' });
    }
  };

  // Finalizar pedido
  const handleFinalizarPedido = async (id) => {
    const confirmado = await showConfirm('¿Está seguro de finalizar este pedido? No podrá cambiar su estado nuevamente.', {
      title: 'Finalizar pedido',
      confirmLabel: 'Finalizar',
    });
    if (!confirmado) return;

    try {
      const response = await pedidoService.finalizarPedido(id);
      showAlert(response.message || 'Pedido finalizado', { type: 'success' });
      cargarPedidos();
      // Si el modal está abierto con este pedido, cerrarlo
      if (modalVerAbierto && pedidoSeleccionado?._id === id) {
        setModalVerAbierto(false);
      }
    } catch (error) {
      console.error('Error finalizando pedido:', error);
      showAlert('Error: ' + (error.response?.data?.message || error.message), { type: 'error' });
    }
  };

  // Generar PDF Nota de Venta para el cliente
  const generarNotaCompra = async (pedido) => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        showAlert('No hay token de autenticación. Por favor, inicie sesión nuevamente.', { type: 'error' });
        return;
      }

      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/pedidos/${pedido._id}/pdf/nota-venta`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        let errorMessage = 'Error al generar PDF';
        try {
          const error = await response.json();
          errorMessage = error.message || errorMessage;
        } catch {
          errorMessage = `Error del servidor: ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      // Obtener el blob del PDF
      const blob = await response.blob();
      
      // Crear URL temporal y descargar
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `NotaVenta_${(pedido.numeroPedido || pedido._id.slice(-8)).toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (error) {
      console.error('Error generando Nota de Venta:', error);
      showAlert('Error al generar la Nota de Venta: ' + error.message, { type: 'error' });
    }
  };

  // Generar PDF Orden de Fabricación (Pedido a Fábrica)
  const generarOrdenFabricacion = async (pedido) => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        showAlert('No hay token de autenticación. Por favor, inicie sesión nuevamente.', { type: 'error' });
        return;
      }

      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/pedidos/${pedido._id}/pdf/pedido-fabrica`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        let errorMessage = 'Error al generar PDF';
        try {
          const error = await response.json();
          errorMessage = error.message || errorMessage;
        } catch {
          errorMessage = `Error del servidor: ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      // Obtener el blob del PDF
      const blob = await response.blob();
      
      // Crear URL temporal y descargar
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PedidoFabrica_${(pedido.numeroPedido || pedido._id.slice(-8)).toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (error) {
      console.error('Error generando Pedido a Fábrica:', error);
      showAlert('Error al generar el Pedido a Fábrica: ' + error.message, { type: 'error' });
    }
  };

  // Filtrar pedidos
  const pedidosFiltrados = useMemo(() => {
    const termBusqueda = busquedaNumero.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');

    return pedidos.filter((pedido) => {
      const cumpleFiltroEstado = filtroEstado === 'todos' || pedido.estado === filtroEstado;
      const cumpleFiltroTipo = filtroTipo === 'todos' || pedido.tipoPedido === filtroTipo;

      if (!cumpleFiltroEstado || !cumpleFiltroTipo) return false;

      if (!termBusqueda) return true;

      // El buscador aplica únicamente a pedidos a fábrica y mixtos (no stock)
      if (pedido.tipoPedido === 'en_stock') return false;

      const numVisible = obtenerNumeroPedidoVisible(pedido);
      const numVisibleLimpio = numVisible.replace(/[^A-Z0-9]/g, '');
      const numRaw = String(pedido.numeroPedido || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

      return numVisibleLimpio.includes(termBusqueda) || numRaw.includes(termBusqueda);
    });
  }, [pedidos, filtroEstado, filtroTipo, busquedaNumero]);

  // Resetear a página 1 cuando cambian los filtros
  useEffect(() => {
    setPaginaActual(1);
  }, [filtroEstado, filtroTipo, busquedaNumero]);

  // Paginación
  const totalPaginas = Math.ceil(pedidosFiltrados.length / itemsPorPagina);
  const indexInicio = (paginaActual - 1) * itemsPorPagina;
  const indexFin = indexInicio + itemsPorPagina;
  const pedidosPaginados = pedidosFiltrados.slice(indexInicio, indexFin);

  // Función para ir a una página específica
  const irAPagina = (pagina) => {
    if (pagina >= 1 && pagina <= totalPaginas) {
      setPaginaActual(pagina);
    }
  };

  const calcularSubtotalAdicionalPorcentual = (producto, presupuesto) => {
    const subtotalBase = Number(presupuesto?.subtotal) || 0;
    const porcentaje = (Number(producto?.precioUnitario) || 0) / 100;
    return Math.round(subtotalBase * porcentaje);
  };

  const obtenerTotalesPresupuesto = (presupuesto) => {
    const subtotal = Number(presupuesto?.subtotal) || 0;

    const adicionalesGuardados = Number(presupuesto?.adicionales);
    const adicionalesCalculados = (presupuesto?.productos || []).reduce((sum, prod) => {
      if (prod.varianteSeleccionada?.categoria === 'ADICIONALES' && prod.varianteSeleccionada?.tipoValor === 'PORCENTUAL') {
        return sum + calcularSubtotalAdicionalPorcentual(prod, presupuesto);
      }
      return sum;
    }, 0);
    const adicionales = Number.isFinite(adicionalesGuardados) ? adicionalesGuardados : adicionalesCalculados;

    const descuento = Number(presupuesto?.descuento) || 0;
    const flete = Number(presupuesto?.flete) || 0;
    const total = Number(presupuesto?.total) || Math.max(0, subtotal + adicionales - descuento + flete);

    return { subtotal, adicionales, descuento, flete, total };
  };

  const formatearEtiquetaAjuste = (nombre, tipo, valor) => {
    if (tipo === 'porcentaje') {
      return `${nombre} (${Number(valor) || 0}%):`;
    }
    return `${nombre}:`;
  };

  const formatearMetodoPago = (metodo) => {
    const etiquetas = {
      efectivo: 'Efectivo',
      transferencia: 'Transferencia',
      tarjeta: 'Tarjeta',
      cheque: 'Cheque',
      otro: 'Otro'
    };
    return etiquetas[metodo] || metodo;
  };

  const obtenerResumenPagosPedido = (pedido) => {
    const total = Number(pedido?.presupuesto?.total) || 0;
    const pagos = Array.isArray(pedido?.pagos) ? pedido.pagos : [];
    const pagado = pagos.reduce((sum, pago) => sum + (Number(pago?.monto) || 0), 0);
    const saldo = Math.max(0, total - pagado);
    return { total, pagado, saldo };
  };

  const handleRegistrarPago = async () => {
    if (!pedidoSeleccionado?._id) return;

    const monto = Number(String(nuevoPago.monto).replace(',', '.'));
    if (!Number.isFinite(monto) || monto <= 0) {
      showAlert('Ingresá un monto válido mayor a 0', { type: 'warning', title: 'Monto inválido' });
      return;
    }

    try {
      setGuardandoPago(true);
      const payload = {
        monto,
        metodo: nuevoPago.metodo,
        fecha: nuevoPago.fecha,
        referencia: (nuevoPago.referencia || '').trim()
      };

      const response = await pedidoService.registrarPago(pedidoSeleccionado._id, payload);
      const pedidoActualizado = response.data || (await pedidoService.getPedidoById(pedidoSeleccionado._id)).data;
      setPedidoSeleccionado(pedidoActualizado);
      await cargarPedidos();

      setNuevoPago((prev) => ({
        ...prev,
        monto: '',
        fecha: new Date().toISOString().split('T')[0],
        referencia: ''
      }));
    } catch (error) {
      console.error('Error registrando pago:', error);
      showAlert('Error: ' + (error.response?.data?.message || error.message), { type: 'error' });
    } finally {
      setGuardandoPago(false);
    }
  };

  const handleDescargarRecibo = async (pedidoId, pagoId) => {
    try {
      await pedidoService.descargarReciboPago(pedidoId, pagoId);
    } catch (error) {
      console.error('Error descargando recibo:', error);
      showAlert('Error al generar recibo: ' + (error.response?.data?.message || error.message), { type: 'error' });
    }
  };

  if (loading) {
    return <LoadingSpinner label="Cargando pedidos..." />;
  }

  return (
    <div className="pedidos-container">
      <div className="pedidos-header">
        <h1>📦 Pedidos</h1>
        <div className="filtros" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="text"
            className="input-busqueda"
            placeholder="Buscar N° pedido (ej: 0038 o P-0038)..."
            value={busquedaNumero}
            onChange={(e) => setBusquedaNumero(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px', width: '240px' }}
          />
          <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
            <option value="todos">Todos los Estados</option>
            <option value="en_proceso">En Proceso</option>
            <option value="a_revisar">A Revisar</option>
            <option value="vencido">Vencido</option>
            <option value="finalizado">Finalizado</option>
            <option value="cancelado">Cancelado</option>
          </select>
          <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
            <option value="todos">Todos los Tipos</option>
            <option value="en_stock">En Stock</option>
            <option value="a_fabrica">A Fábrica</option>
            <option value="mixto">Mixto</option>
          </select>
        </div>
      </div>

      <div className="pedidos-tabla">
        <table>
          <thead>
            <tr>
              <th>N°</th>
              <th>CLIENTE</th>
              <th>SUCURSAL</th>
              <th>TIPO</th>
              <th>TOTAL</th>
              <th>ESTADO</th>
              <th>FECHA</th>
              <th>ACCIONES</th>
            </tr>
          </thead>
          <tbody>
            {pedidosPaginados.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center">
                  No hay pedidos para mostrar
                </td>
              </tr>
            ) : (
              pedidosPaginados.map((pedido) => (
                <tr key={pedido._id}>
                  <td>
                    {obtenerNumeroPedidoVisible(pedido) || '-'}
                  </td>
                  <td>{pedido.presupuesto?.cliente?.nombre || '-'}</td>
                  <td>{pedido.sucursal?.toUpperCase() || '-'}</td>
                  <td>
                    <span className={`tipo-badge tipo-${pedido.tipoPedido}`}>
                      {pedido.tipoPedido === 'en_stock' ? '📦 EN STOCK' : 
                       pedido.tipoPedido === 'a_fabrica' ? '🏭 A FÁBRICA' : 
                       '🔀 MIXTO'}
                    </span>
                  </td>
                  <td>${pedido.presupuesto?.total?.toLocaleString() || 0}</td>
                  <td>
                    <span className={`estado-badge estado-${pedido.estado}`}>
                      {pedido.estado?.replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td>{new Date(pedido.createdAt).toLocaleDateString()}</td>
                  <td className="acciones-cell">
                    <button 
                      className="btn-icon" 
                      title="Ver Detalle"
                      onClick={() => handleVerPedido(pedido._id)}
                    >
                      👁️
                    </button>
                    <button 
                      className="btn-icon btn-pdf" 
                      title="Nota de Venta"
                      onClick={() => generarNotaCompra(pedido)}
                    >
                      📄
                    </button>
                    {(pedido.tipoPedido === 'a_fabrica' || pedido.tipoPedido === 'mixto') && (
                      <button 
                        className="btn-icon btn-pdf" 
                        title="Orden de Fabricación"
                        onClick={() => generarOrdenFabricacion(pedido)}
                      >
                        🏭
                      </button>
                    )}
                    {pedido.estado !== 'cancelado' && pedido.estado !== 'finalizado' && !pedido.estadoFinalizado && (
                      <button 
                        className="btn-icon btn-check" 
                        title="Finalizar Pedido"
                        onClick={() => handleFinalizarPedido(pedido._id)}
                      >
                        ✓
                      </button>
                    )}
                    {pedido.estado !== 'cancelado' && pedido.estado !== 'finalizado' && (
                      <button 
                        className="btn-icon btn-cancel" 
                        title="Cancelar Pedido"
                        onClick={() => handleCancelarPedido(pedido._id)}
                      >
                        ❌
                      </button>
                    )}
                    {pedido.estado === 'cancelado' && (
                      <button
                        className="btn-icon btn-cancel"
                        title="Eliminar Pedido Cancelado"
                        onClick={() => handleEliminarPedidoCancelado(pedido._id)}
                      >
                        ❌
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Controles de paginación */}
      {pedidosFiltrados.length > 0 && (
        <div className="paginacion-container">
          <div className="paginacion-info">
            Mostrando {indexInicio + 1} - {Math.min(indexFin, pedidosFiltrados.length)} de {pedidosFiltrados.length} pedidos
          </div>
          
          <div className="paginacion-controles">
            <button 
              onClick={() => irAPagina(1)}
              disabled={paginaActual === 1}
              className="btn-paginacion"
              title="Primera página"
            >
              ««
            </button>
            
            <button 
              onClick={() => irAPagina(paginaActual - 1)}
              disabled={paginaActual === 1}
              className="btn-paginacion"
              title="Página anterior"
            >
              ‹
            </button>
            
            <div className="paginacion-paginas">
              {Array.from({ length: totalPaginas }, (_, i) => i + 1)
                .filter(pagina => {
                  // Mostrar solo páginas cercanas a la actual
                  return pagina === 1 || 
                         pagina === totalPaginas || 
                         (pagina >= paginaActual - 2 && pagina <= paginaActual + 2);
                })
                .map((pagina, index, array) => (
                  <React.Fragment key={pagina}>
                    {index > 0 && array[index - 1] !== pagina - 1 && (
                      <span className="paginacion-ellipsis">...</span>
                    )}
                    <button
                      onClick={() => irAPagina(pagina)}
                      className={`btn-paginacion ${paginaActual === pagina ? 'active' : ''}`}
                    >
                      {pagina}
                    </button>
                  </React.Fragment>
                ))
              }
            </div>
            
            <button 
              onClick={() => irAPagina(paginaActual + 1)}
              disabled={paginaActual === totalPaginas}
              className="btn-paginacion"
              title="Página siguiente"
            >
              ›
            </button>
            
            <button 
              onClick={() => irAPagina(totalPaginas)}
              disabled={paginaActual === totalPaginas}
              className="btn-paginacion"
              title="Última página"
            >
              »»
            </button>
          </div>

          <div className="paginacion-selector">
            <label>Items por página:</label>
            <select 
              value={itemsPorPagina} 
              onChange={(e) => {
                setItemsPorPagina(Number(e.target.value));
                setPaginaActual(1);
              }}
              className="select-items-pagina"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      )}

      {/* Modal ver pedido */}
      {modalVerAbierto && pedidoSeleccionado && (
        <div className="modal-overlay" onClick={() => setModalVerAbierto(false)}>
          <div className="modal-content modal-pedido" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Detalle del Pedido</h2>
              <button className="modal-close" onClick={() => setModalVerAbierto(false)}>×</button>
            </div>
            
            <div className="modal-body">
              {/* Información del pedido */}
              <div className="pedido-info">
                <div className="info-section">
                  <h3>Información General</h3>
                  {obtenerNumeroPedidoVisible(pedidoSeleccionado) && (
                    <div className="info-row">
                      <strong>N° de Pedido:</strong> {obtenerNumeroPedidoVisible(pedidoSeleccionado)}
                    </div>
                  )}
                  <div className="info-row">
                    <strong>Cliente:</strong> {pedidoSeleccionado.presupuesto?.cliente?.nombre || '-'}
                  </div>
                  <div className="info-row">
                    <strong>Teléfono:</strong> {pedidoSeleccionado.presupuesto?.cliente?.telefono || '-'}
                  </div>
                  <div className="info-row">
                    <strong>Sucursal:</strong> {pedidoSeleccionado.sucursal?.toUpperCase() || '-'}
                  </div>
                  <div className="info-row">
                    <strong>Tipo de Pedido:</strong>
                    <span className={`tipo-badge tipo-${pedidoSeleccionado.tipoPedido}`}>
                      {pedidoSeleccionado.tipoPedido === 'en_stock' ? '📦 EN STOCK' : 
                       pedidoSeleccionado.tipoPedido === 'a_fabrica' ? '🏭 A FÁBRICA' : 
                       '🔀 MIXTO'}
                    </span>
                  </div>
                  <div className="info-row">
                    <strong>Estado:</strong>
                    <span className={`estado-badge estado-${pedidoSeleccionado.estado}`}>
                      {pedidoSeleccionado.estado?.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  <div className="info-row">
                    <strong>Fecha de Creación:</strong> {new Date(pedidoSeleccionado.createdAt).toLocaleDateString()}
                  </div>
                  {pedidoSeleccionado.fechaLimite && (
                    <div className="info-row">
                      <strong>Fecha Límite:</strong> 
                      <span style={{ 
                        color: new Date(pedidoSeleccionado.fechaLimite) < new Date() ? 'red' : 'green',
                        fontWeight: 'bold'
                      }}>
                        {new Date(pedidoSeleccionado.fechaLimite).toLocaleDateString()}
                        ({Math.ceil((new Date(pedidoSeleccionado.fechaLimite) - new Date()) / (1000 * 60 * 60 * 24))} días)
                      </span>
                    </div>
                  )}
                  <div className="info-row">
                    <strong>Forma de Pago:</strong> {pedidoSeleccionado.presupuesto?.formaPago?.replace('_', ' ').toUpperCase() || '-'}
                  </div>
                </div>

                <div className="info-column">
                  {/* Editor de fecha límite y finalización */}
                  {pedidoSeleccionado.estado !== 'cancelado' && !pedidoSeleccionado.estadoFinalizado && (
                    <div className="info-section">
                      {/* Editor de días límite (solo para A_FABRICA y MIXTO, y solo para admin) */}
                      {user?.rol === 'admin' && (pedidoSeleccionado.tipoPedido === 'a_fabrica' || pedidoSeleccionado.tipoPedido === 'mixto') && (
                        <div className="dias-limite-editor">
                          <h3>Modificar Fecha Límite</h3>
                          <div className="dias-limite-input-group">
                            <input
                              type="number"
                              min="1"
                              placeholder={pedidoSeleccionado.diasLimite || "Días desde creación"}
                              value={diasLimiteTemp}
                              onChange={(e) => setDiasLimiteTemp(e.target.value)}
                            />
                            <button 
                              className="btn-guardar-dias"
                              onClick={async () => {
                                if (!diasLimiteTemp || diasLimiteTemp < 1) {
                                  await showAlert('Debe ingresar al menos 1 día', { type: 'warning', title: 'Valor inválido' });
                                  return;
                                }
                                handleActualizarDiasLimite(pedidoSeleccionado._id, parseInt(diasLimiteTemp));
                                setDiasLimiteTemp('');
                              }}
                            >
                              💾 Guardar
                            </button>
                          </div>
                          {pedidoSeleccionado.diasLimite && (
                            <small style={{ color: '#666', marginTop: '8px', display: 'block' }}>
                              Actual: {pedidoSeleccionado.diasLimite} días (desde {new Date(pedidoSeleccionado.createdAt).toLocaleDateString()})
                            </small>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {pedidoSeleccionado.estadoFinalizado && (
                    <div className="info-section" style={{ backgroundColor: '#d4edda', padding: '15px', borderRadius: '8px' }}>
                      <p style={{ margin: 0, color: '#155724', fontWeight: 'bold' }}>
                        ✓ Este pedido ha sido finalizado
                      </p>
                    </div>
                  )}

                  <div className="info-section pagos-section">
                    <h3>Pagos</h3>
                    {(() => {
                      const resumen = obtenerResumenPagosPedido(pedidoSeleccionado);
                      return (
                        <>
                          <div className="pagos-resumen">
                            <div className="pagos-chip">Total: ${resumen.total.toLocaleString()}</div>
                            <div className="pagos-chip">Pagado: ${resumen.pagado.toLocaleString()}</div>
                            <div className="pagos-chip saldo">Saldo: ${resumen.saldo.toLocaleString()}</div>
                          </div>

                          {pedidoSeleccionado.estado !== 'cancelado' && resumen.saldo > 0 && (
                            <div className="pagos-form-minimal">
                              <input
                                type="text"
                                inputMode="decimal"
                                placeholder="Monto"
                                value={nuevoPago.monto}
                                onChange={(e) => {
                                  const valor = e.target.value;
                                  if (valor === '' || /^\d*([.,]\d{0,2})?$/.test(valor)) {
                                    setNuevoPago((prev) => ({ ...prev, monto: valor }));
                                  }
                                }}
                              />
                              <select
                                value={nuevoPago.metodo}
                                onChange={(e) => setNuevoPago((prev) => ({ ...prev, metodo: e.target.value }))}
                              >
                                <option value="efectivo">Efectivo</option>
                                <option value="transferencia">Transferencia</option>
                                <option value="tarjeta">Tarjeta</option>
                                <option value="cheque">Cheque</option>
                                <option value="otro">Otro</option>
                              </select>
                              <input
                                type="date"
                                value={nuevoPago.fecha}
                                onChange={(e) => setNuevoPago((prev) => ({ ...prev, fecha: e.target.value }))}
                              />
                              <input
                                type="text"
                                placeholder="Referencia"
                                value={nuevoPago.referencia}
                                onChange={(e) => setNuevoPago((prev) => ({ ...prev, referencia: e.target.value }))}
                              />
                              <button
                                type="button"
                                className="btn-guardar-pago"
                                onClick={handleRegistrarPago}
                                disabled={guardandoPago}
                              >
                                {guardandoPago ? 'Guardando...' : 'Agregar'}
                              </button>
                            </div>
                          )}

                          <div className="pagos-lista-minimal">
                            {(pedidoSeleccionado.pagos || []).length === 0 ? (
                              <div className="pago-vacio">Sin pagos registrados</div>
                            ) : (
                              [...(pedidoSeleccionado.pagos || [])]
                                .sort((a, b) => new Date(b.fecha || b.createdAt) - new Date(a.fecha || a.createdAt))
                                .map((pago) => (
                                  <div className="pago-item" key={pago._id}>
                                    <div className="pago-main">
                                      <strong>${(Number(pago.monto) || 0).toLocaleString()}</strong>
                                      <span>{formatearMetodoPago(pago.metodo)}</span>
                                      <span>{new Date(pago.fecha || pago.createdAt).toLocaleDateString()}</span>
                                      {pago.referencia ? <span className="pago-ref">Ref: {pago.referencia}</span> : null}
                                    </div>
                                    <button
                                      type="button"
                                      className="btn-recibo-mini"
                                      onClick={() => handleDescargarRecibo(pedidoSeleccionado._id, pago._id)}
                                      title="Descargar recibo"
                                    >
                                      Recibo
                                    </button>
                                  </div>
                                ))
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Productos */}
              <div className="productos-pedido">
                <h3>Productos del Pedido</h3>
                
                {(() => {
                  // Agrupar productos por categoría
                  const productosPorCategoria = {};
                  pedidoSeleccionado.presupuesto?.productos?.forEach(prod => {
                    const categoria = prod.varianteSeleccionada?.categoria || prod.catalogoRef?.categoria || 'OTROS';
                    if (!productosPorCategoria[categoria]) {
                      productosPorCategoria[categoria] = [];
                    }
                    productosPorCategoria[categoria].push(prod);
                  });

                  const ordenCategorias = ['MUEBLES', 'SOFA', 'SILLA', 'RESPALDO', 'DECORACION', 'ADICIONALES'];
                  
                  return ordenCategorias.map(categoria => {
                    const productos = productosPorCategoria[categoria];
                    if (!productos || productos.length === 0) return null;

                    return (
                      <div key={categoria} style={{ marginBottom: '30px' }}>
                        <h4 style={{ marginBottom: '10px', color: '#2c3e50' }}>{categoria}</h4>
                        <div className="tabla-scroll">
                        <table>
                          <thead>
                            <tr>
                              {categoria === 'MUEBLES' && (
                                <>
                                  <th>TIPO</th>
                                  <th>MODELO</th>
                                  <th>MEDIDAS</th>
                                  <th>MADERA</th>
                                  <th>LUSTRE</th>
                                  <th>DESTINO</th>
                                  <th>CANTIDAD</th>
                                  <th>PRECIO UNIT.</th>
                                  <th>SUBTOTAL</th>
                                </>
                              )}
                              {categoria === 'SOFA' && (
                                <>
                                  <th>TIPO</th>
                                  <th>MODELO</th>
                                  <th>MEDIDAS</th>
                                  <th>CANT. TAPIZADO</th>
                                  <th>MATERIAL</th>
                                  <th>DESTINO</th>
                                  <th>CANTIDAD</th>
                                  <th>PRECIO UNIT.</th>
                                  <th>SUBTOTAL</th>
                                </>
                              )}
                              {categoria === 'SILLA' && (
                                <>
                                  <th>TIPO</th>
                                  <th>MODELO</th>
                                  <th>MADERA</th>
                                  <th>TAPIZADO</th>
                                  <th>CANT. TAPIZADO</th>
                                  <th>LUSTRE</th>
                                  <th>DESTINO</th>
                                  <th>CANTIDAD</th>
                                  <th>PRECIO UNIT.</th>
                                  <th>SUBTOTAL</th>
                                </>
                              )}
                              {categoria === 'RESPALDO' && (
                                <>
                                  <th>TIPO</th>
                                  <th>MODELO</th>
                                  <th>MEDIDAS</th>
                                  <th>MADERA</th>
                                  <th>TAPIZADO</th>
                                  <th>CANT. TAPIZADO</th>
                                  <th>LUSTRE</th>
                                  <th>DESTINO</th>
                                  <th>CANTIDAD</th>
                                  <th>PRECIO UNIT.</th>
                                  <th>SUBTOTAL</th>
                                </>
                              )}
                              {categoria === 'DECORACION' && (
                                <>
                                  <th>TIPO</th>
                                  <th>MODELO</th>
                                  <th>SKU</th>
                                  <th>PROVEEDOR</th>
                                  <th>DESTINO</th>
                                  <th>CANTIDAD</th>
                                  <th>PRECIO UNIT.</th>
                                  <th>SUBTOTAL</th>
                                </>
                              )}
                              {categoria === 'ADICIONALES' && (
                                <>
                                  <th>NOMBRE</th>
                                  <th>TIPO VALOR</th>
                                  <th>VALOR</th>
                                  <th>DESTINO</th>
                                  <th>CANTIDAD</th>
                                  <th>PRECIO UNIT.</th>
                                  <th>SUBTOTAL</th>
                                </>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {productos.map((prod, idx) => (
                              <tr key={idx}>
                                {categoria === 'MUEBLES' && (
                                  <>
                                    <td>{prod.varianteSeleccionada?.tipo || prod.catalogoRef?.tipo || '-'}</td>
                                    <td>{prod.varianteSeleccionada?.modelo || prod.catalogoRef?.modelo || '-'}</td>
                                    <td>{prod.varianteSeleccionada?.medidas || '-'}</td>
                                    <td>{prod.varianteSeleccionada?.madera || '-'}</td>
                                    <td>{prod.varianteSeleccionada?.lustre || '-'}</td>
                                    <td style={{ textAlign: 'center' }}>
                                      <span className={`tipo-badge tipo-${prod.fabricaManual ? 'a_fabrica' : 'en_stock'}`}>
                                        {prod.fabricaManual ? '🏭 FÁBRICA' : '📦 STOCK'}
                                      </span>
                                    </td>
                                    <td>{prod.cantidad}</td>
                                    <td>${prod.precioUnitario?.toLocaleString() || 0}</td>
                                    <td>${prod.subtotal?.toLocaleString() || 0}</td>
                                  </>
                                )}
                                {categoria === 'SOFA' && (
                                  <>
                                    <td>{prod.varianteSeleccionada?.tipo || prod.catalogoRef?.tipo || '-'}</td>
                                    <td>{prod.varianteSeleccionada?.modelo || prod.catalogoRef?.modelo || '-'}</td>
                                    <td>{prod.varianteSeleccionada?.medidas || '-'}</td>
                                    <td>{prod.varianteSeleccionada?.cantidadTapizado || '-'}</td>
                                    <td>{prod.varianteSeleccionada?.lustre || '-'}</td>
                                    <td style={{ textAlign: 'center' }}>
                                      <span className={`tipo-badge tipo-${prod.fabricaManual ? 'a_fabrica' : 'en_stock'}`}>
                                        {prod.fabricaManual ? '🏭 FÁBRICA' : '📦 STOCK'}
                                      </span>
                                    </td>
                                    <td>{prod.cantidad}</td>
                                    <td>${prod.precioUnitario?.toLocaleString() || 0}</td>
                                    <td>${prod.subtotal?.toLocaleString() || 0}</td>
                                  </>
                                )}
                                {categoria === 'SILLA' && (
                                  <>
                                    <td>{prod.varianteSeleccionada?.tipo || prod.catalogoRef?.tipo || '-'}</td>
                                    <td>{prod.varianteSeleccionada?.modelo || prod.catalogoRef?.modelo || '-'}</td>
                                    <td>{prod.varianteSeleccionada?.madera || '-'}</td>
                                    <td>{prod.varianteSeleccionada?.tapizado || '-'}</td>
                                    <td>{prod.varianteSeleccionada?.cantidadTapizado || '-'}</td>
                                    <td>{prod.varianteSeleccionada?.lustre || '-'}</td>
                                    <td style={{ textAlign: 'center' }}>
                                      <span className={`tipo-badge tipo-${prod.fabricaManual ? 'a_fabrica' : 'en_stock'}`}>
                                        {prod.fabricaManual ? '🏭 FÁBRICA' : '📦 STOCK'}
                                      </span>
                                    </td>
                                    <td>{prod.cantidad}</td>
                                    <td>${prod.precioUnitario?.toLocaleString() || 0}</td>
                                    <td>${prod.subtotal?.toLocaleString() || 0}</td>
                                  </>
                                )}
                                {categoria === 'RESPALDO' && (
                                  <>
                                    <td>{prod.varianteSeleccionada?.tipo || prod.catalogoRef?.tipo || '-'}</td>
                                    <td>{prod.varianteSeleccionada?.modelo || prod.catalogoRef?.modelo || '-'}</td>
                                    <td>{prod.varianteSeleccionada?.medidas || '-'}</td>
                                    <td>{prod.varianteSeleccionada?.madera || '-'}</td>
                                    <td>{prod.varianteSeleccionada?.tapizado || '-'}</td>
                                    <td>{prod.varianteSeleccionada?.cantidadTapizado || '-'}</td>
                                    <td>{prod.varianteSeleccionada?.lustre || '-'}</td>
                                    <td style={{ textAlign: 'center' }}>
                                      <span className={`tipo-badge tipo-${prod.fabricaManual ? 'a_fabrica' : 'en_stock'}`}>
                                        {prod.fabricaManual ? '🏭 FÁBRICA' : '📦 STOCK'}
                                      </span>
                                    </td>
                                    <td>{prod.cantidad}</td>
                                    <td>${prod.precioUnitario?.toLocaleString() || 0}</td>
                                    <td>${prod.subtotal?.toLocaleString() || 0}</td>
                                  </>
                                )}
                                {categoria === 'DECORACION' && (
                                  <>
                                    <td>{prod.varianteSeleccionada?.tipo || prod.catalogoRef?.tipo || '-'}</td>
                                    <td>{prod.varianteSeleccionada?.modelo || prod.catalogoRef?.modelo || '-'}</td>
                                    <td>{prod.varianteSeleccionada?.sku || '-'}</td>
                                    <td>{prod.varianteSeleccionada?.proveedor || '-'}</td>
                                    <td style={{ textAlign: 'center' }}>
                                      <span className={`tipo-badge tipo-${prod.fabricaManual ? 'a_fabrica' : 'en_stock'}`}>
                                        {prod.fabricaManual ? '🏭 FÁBRICA' : '📦 STOCK'}
                                      </span>
                                    </td>
                                    <td>{prod.cantidad}</td>
                                    <td>${prod.precioUnitario?.toLocaleString() || 0}</td>
                                    <td>${prod.subtotal?.toLocaleString() || 0}</td>
                                  </>
                                )}
                                {categoria === 'ADICIONALES' && (
                                  <>
                                    <td>{prod.varianteSeleccionada?.modelo || prod.catalogoRef?.modelo || '-'}</td>
                                    <td>{prod.varianteSeleccionada?.tipoValor || '-'}</td>
                                    <td>
                                      {prod.varianteSeleccionada?.tipoValor === 'PORCENTUAL' 
                                        ? `${prod.varianteSeleccionada?.costo || 0}%`
                                        : `$${(prod.varianteSeleccionada?.costo || 0).toLocaleString()}`
                                      }
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                      <span className={`tipo-badge tipo-${prod.fabricaManual ? 'a_fabrica' : 'en_stock'}`}>
                                        {prod.fabricaManual ? '🏭 FÁBRICA' : '📦 STOCK'}
                                      </span>
                                    </td>
                                    <td>{prod.cantidad}</td>
                                    <td>${prod.precioUnitario?.toLocaleString() || 0}</td>
                                    <td>
                                      ${(
                                        prod.varianteSeleccionada?.tipoValor === 'PORCENTUAL'
                                          ? calcularSubtotalAdicionalPorcentual(prod, pedidoSeleccionado.presupuesto)
                                          : (prod.subtotal || 0)
                                      ).toLocaleString()}
                                    </td>
                                  </>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        </div>
                      </div>
                    );
                  });
                })()}

                <div className="totales">
                  {(() => {
                    const totales = obtenerTotalesPresupuesto(pedidoSeleccionado.presupuesto);
                    return (
                      <>
                  <div className="total-row">
                    <span>Subtotal:</span>
                    <span>${totales.subtotal.toLocaleString()}</span>
                  </div>
                  {totales.adicionales > 0 && (
                    <div className="total-row">
                      <span>Adicionales (%):</span>
                      <span>${totales.adicionales.toLocaleString()}</span>
                    </div>
                  )}
                  {totales.descuento > 0 && (
                    <div className="total-row" style={{ color: '#1f7a45' }}>
                      <span>{formatearEtiquetaAjuste('Bonificacion', pedidoSeleccionado.presupuesto?.descuentoTipo || 'monto', pedidoSeleccionado.presupuesto?.descuentoValor || 0)}</span>
                      <span>-${totales.descuento.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="total-row">
                    <span>{formatearEtiquetaAjuste('Flete', pedidoSeleccionado.presupuesto?.fleteTipo || 'monto', pedidoSeleccionado.presupuesto?.fleteValor || 0)}</span>
                    <span>${totales.flete.toLocaleString()}</span>
                  </div>
                  <div className="total-row total-final">
                    <span>TOTAL:</span>
                    <span>${totales.total.toLocaleString()}</span>
                  </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {pedidoSeleccionado.observaciones && (
                <div className="pedido-observaciones">
                  <strong>Observaciones:</strong>
                  <p>{pedidoSeleccionado.observaciones}</p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setModalVerAbierto(false)}>
                Cerrar
              </button>
              <button className="btn-pdf-large" onClick={() => generarNotaCompra(pedidoSeleccionado)}>
                📄 Nota de Venta
              </button>
              {(pedidoSeleccionado.tipoPedido === 'a_fabrica' || pedidoSeleccionado.tipoPedido === 'mixto') && (
                <button className="btn-pdf-large" onClick={() => generarOrdenFabricacion(pedidoSeleccionado)}>
                  🏭 Orden de Fabricación
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pedidos;
